import { Injectable } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CloudflareTurnstileService {
  private siteKey: string | null = null;
  private token: string | null = null;
  private tokenExpiryMs: number | null = null;
  private expiryTimer: any = null;
  private scriptLoaded = false;
  private scriptLoadingPromise: Promise<void> | null = null;

  private shouldRenderSubject = new BehaviorSubject<boolean>(false);
  public shouldRender$ = this.shouldRenderSubject.asObservable();

  constructor(private configService: ConfigService, private router: Router) {
    // restore token from sessionStorage (survive page navigation/refresh)
    this.restoreTokenFromStorage();

    this.configService
      .getConfig()
      .pipe(take(1))
      .subscribe((config: any) => {
        this.siteKey = config.cloudflareTurnstileSiteKey || null;
        if (this.siteKey) {
          // Preload script so it's ready if component needs to render
          this.loadTurnstileScript();
        }
        this.computeShouldRender();
      });
  }

  private restoreTokenFromStorage() {
    try {
      const rawToken = sessionStorage.getItem('turnstileToken');
      const rawExpiry = sessionStorage.getItem('turnstileTokenExpiry');
      if (rawToken && rawExpiry) {
        const expiry = parseInt(rawExpiry, 10);
        if (!isNaN(expiry) && expiry > Date.now()) {
          this.token = rawToken;
          this.tokenExpiryMs = expiry;
          this.startExpiryTimer();
        } else {
          this.clearToken();
        }
      }
    } catch (e) {
      // ignore storage errors
    }
  }

  private persistTokenToStorage() {
    try {
      if (this.token && this.tokenExpiryMs) {
        sessionStorage.setItem('turnstileToken', this.token);
        sessionStorage.setItem('turnstileTokenExpiry', String(this.tokenExpiryMs));
      } else {
        sessionStorage.removeItem('turnstileToken');
        sessionStorage.removeItem('turnstileTokenExpiry');
      }
    } catch (e) {
      // ignore storage errors
    }
  }

  private computeShouldRender() {
    const should = !!this.siteKey && !this.isTokenValid();
    this.shouldRenderSubject.next(should);
  }

  public isTokenValid(): boolean {
    return !!this.token && !!this.tokenExpiryMs && this.tokenExpiryMs > Date.now();
  }

  public getToken(): string | null {
    if (this.isTokenValid()) return this.token;
    return null;
  }

  // Cloudflare siteverify accepts a token once, so a request that sends it takes it out of
  // circulation; clearing it makes the widget fetch a fresh one for the next request.
  public consumeToken(): string | null {
    const token = this.getToken();
    if (token) this.clearToken();
    return token;
  }

  public getSiteKey(): string | null {
    return this.siteKey;
  }

  public async loadTurnstileScript(): Promise<void> {
    if (this.scriptLoaded) return Promise.resolve();
    if (this.scriptLoadingPromise) return this.scriptLoadingPromise;

    this.scriptLoadingPromise = new Promise<void>((resolve, reject) => {
      // Avoid adding multiple script tags
      if (document.querySelector('script[data-turnstile]')) {
        this.scriptLoaded = true;
        resolve();
        return;
      }
      const turnstileScript = document.createElement('script');
      turnstileScript.setAttribute('src', 'https://challenges.cloudflare.com/turnstile/v0/api.js');
      turnstileScript.setAttribute('data-turnstile', '1');
      turnstileScript.async = true;
      turnstileScript.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      turnstileScript.onerror = (err) => {
        reject(err);
      };
      document.head.appendChild(turnstileScript);
    });

    return this.scriptLoadingPromise;
  }

  public setToken(token: string, ttlSeconds = 120) {
    this.token = token;
    this.tokenExpiryMs = Date.now() + ttlSeconds * 1000;
    this.persistTokenToStorage();
    this.startExpiryTimer();
    this.computeShouldRender();
  }

  private startExpiryTimer() {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    if (this.tokenExpiryMs) {
      const ms = this.tokenExpiryMs - Date.now();
      if (ms > 0) {
        this.expiryTimer = setTimeout(() => {
          this.clearToken();
        }, ms);
      } else {
        this.clearToken();
      }
    }
  }

  public clearToken() {
    this.token = null;
    this.tokenExpiryMs = null;
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    this.persistTokenToStorage();
    this.computeShouldRender();
  }
}
